import { Link } from "react-router-dom";
import { ArrowRight, MailCheck } from "lucide-react";
import Reveal from "@/components/Reveal";

const STEPS = [
  { n: "1", body: "Customer selects the gift card, denomination, redemption region, and currency." },
  { n: "2", body: "Customer completes payment." },
  { n: "3", body: "Payment is confirmed." },
  { n: "4", body: "Zeyinx verifies the payment and prepares the unique gift-card code." },
  { n: "5", body: "The code is sent to the checkout email address and appears in the customer's order details when logged in." },
];

export default function HowItWorks() {
  return (
    <div className="vault-container pt-16 sm:pt-20" data-testid="how-it-works-page">
      <Reveal>
        <p className="overline-label">How it works</p>
        <h1 className="mt-3 max-w-2xl font-display text-4xl font-semibold tracking-tighter sm:text-5xl">
          Three steps between you and your code.
        </h1>
      </Reveal>

      <div className="mt-16 flex flex-col">
        {[
          {
            n: "01",
            title: "Choose your card",
            body: "Browse five launch brands. Your redemption region is always visible in the header and on every product page — your account region determines which gift-card codes you can redeem. Currency only changes how prices are displayed and paid.",
          },
          {
            n: "02",
            title: "Pay securely",
            body: "Checkout is handled by Razorpay. Review the highlighted confirmation panel — product, value, redemption region, delivery method — before you pay. Guest checkout is the default.",
          },
          {
            n: "03",
            title: "Receive your code",
            body: "Payment confirmed. We verify the payment, then send your digital code to your email — usually within a few hours, always within 24.",
          },
        ].map((c, i) => (
          <Reveal key={c.n} delay={i * 0.06}>
            <div className="grid gap-4 border-t border-white/[0.07] py-12 sm:grid-cols-[120px_1fr_1.2fr] sm:items-baseline">
              <span className="font-display text-5xl font-semibold tracking-tight text-white/[0.08]">{c.n}</span>
              <h2 className="font-display text-2xl font-semibold tracking-tight">{c.title}</h2>
              <p className="max-w-lg text-sm leading-relaxed text-muted-foreground">{c.body}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal>
        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          <div className="rounded-2xl border border-white/[0.08] bg-card p-7" data-testid="delivery-method-card">
            <MailCheck className="h-5 w-5 text-primary" />
            <h3 className="mt-4 font-display text-lg font-semibold tracking-tight">Delivery</h3>
            <dl className="mt-4 flex flex-col gap-3 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Delivery method</dt>
                <dd className="mt-1">Digital code sent by email</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Estimated delivery</dt>
                <dd className="mt-1">After payment verification — usually within a few hours, always within 24 hours.</dd>
              </div>
            </dl>
          </div>
          <div className="rounded-2xl border border-white/[0.08] bg-card p-7" data-testid="flow-card">
            <h3 className="font-display text-lg font-semibold tracking-tight">The flow, end to end</h3>
            <ol className="mt-4 flex flex-col gap-3">
              {STEPS.map((s) => (
                <li key={s.n} className="flex gap-3 text-sm text-muted-foreground">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-primary/40 font-mono text-[10px] text-primary">
                    {s.n}
                  </span>
                  {s.body}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </Reveal>

      <Reveal>
        <div className="mt-16 text-center">
          <Link
            to="/cards"
            data-testid="hiw-browse-button"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-semibold text-primary-foreground transition-transform duration-200 hover:scale-[1.03]"
          >
            Browse gift cards <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Reveal>
    </div>
  );
}
